import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  // Layout & Navigation
  Mountain, Grid3X3, Image as ImageIcon, Plus, Save, Trash2, Edit2, X, 
  Upload, Loader2, Eye, EyeOff, ChevronDown, ChevronUp,
  FolderOpen, LayoutGrid, Layers, Box, Square, Circle, Triangle, Hexagon, Octagon,
  
  // Sports & Activities
  Bike, Dumbbell, Trophy, Medal, Target, Flame, Zap, Activity, 
  Footprints, PersonStanding, Users, Rocket,
  Snowflake, Waves, Anchor, Compass, Flag, CircleDot,
  
  // Facilities & Amenities
  Wifi, Coffee, Car, Utensils, Lock, Music, Camera, Shield, Heart, Star,
  Droplets, Wind, Sun, Moon, Thermometer, Accessibility, Phone, Sparkles,
  MapPin, Clock, CreditCard, Gift, ShoppingBag, Award, Crown, Gem,
  Home, Building, Building2, Castle, Store, Hotel, Warehouse,
  Baby, ParkingSquare, Shirt, Headphones, Mic, Video, Tv, Gamepad2, 
  Pizza, Beer, Apple, Wrench, Hammer, Settings, Cog,
  
  // Information & Status
  Info, HelpCircle, CheckCircle, AlertCircle, AlertTriangle, Bell, BellRing,
  MessageCircle, Mail, Send, Bookmark, Tag,
  
  // Nature & Environment
  TreePine, Trees, Flower, Flower2, Leaf, Sprout,
  CloudSun, CloudRain, Rainbow, Sunrise, Sunset,
  
  // Transportation
  Bus, Train, Plane, Ship, Truck, CarFront,
  
  // Objects & Tools
  Key, Lightbulb, Flashlight, Lamp, Battery, BatteryCharging,
  Scissors, Paintbrush, Palette, Ruler, Calculator,
  Glasses, Watch, Briefcase, Backpack, Luggage, Package,
  
  // Entertainment & Media
  Film, Radio, Speaker, Volume2, Play, Pause,
  Ticket, PartyPopper, Cake,
  
  // Sports Equipment
  Disc, Timer, Hourglass
} from 'lucide-react';
import { toast } from 'sonner';
import SortableList from '../../components/admin/SortableList';
import FlagIcon from '../../components/FlagIcon';

const API = process.env.REACT_APP_BACKEND_URL;

// All available icons with their components - EXTENDED LIST (~100 icons)
const ICON_MAP = {
  // Sports & Activities
  Bike, Dumbbell, Trophy, Medal, Target, Flame, Zap, Activity, 
  Footprints, PersonStanding, Users, Rocket,
  Snowflake, Waves, Anchor, Compass, Flag, CircleDot,
  
  // Facilities & Amenities
  Wifi, Coffee, Car, Utensils, Lock, Music, Camera, Shield, Heart, Star,
  Droplets, Wind, Sun, Moon, Thermometer, Accessibility, Phone, Sparkles,
  MapPin, Clock, CreditCard, Gift, ShoppingBag, Award, Crown, Gem,
  Home, Building, Building2, Castle, Store, Hotel, Warehouse,
  Baby, ParkingSquare, Shirt, Headphones, Mic, Video, Tv, Gamepad2, 
  Pizza, Beer, Apple, Wrench, Hammer, Settings, Cog,
  
  // Shapes & Layout
  Mountain, Grid3X3, ImageIcon, Box, Square, Circle, Triangle, Hexagon, Octagon,
  LayoutGrid, Layers,
  
  // Information & Status
  Info, HelpCircle, CheckCircle, AlertCircle, AlertTriangle, Bell, BellRing,
  MessageCircle, Mail, Send, Bookmark, Tag,
  
  // Nature & Environment
  TreePine, Trees, Flower, Flower2, Leaf, Sprout,
  CloudSun, CloudRain, Rainbow, Sunrise, Sunset,
  
  // Transportation
  Bus, Train, Plane, Ship, Truck, CarFront,
  
  // Objects & Tools
  Key, Lightbulb, Flashlight, Lamp, Battery, BatteryCharging,
  Scissors, Paintbrush, Palette, Ruler, Calculator,
  Glasses, Watch, Briefcase, Backpack, Luggage, Package,
  
  // Entertainment & Media
  Film, Radio, Speaker, Volume2, Play, Pause,
  Ticket, PartyPopper, Cake,
  
  // Sports Equipment
  Disc, Timer, Hourglass
};

const AVAILABLE_ICONS = Object.keys(ICON_MAP);

// Helper to render icon by name
const IconComponent = ({ name, className = "w-5 h-5" }) => {
  const Icon = ICON_MAP[name] || Star;
  return <Icon className={className} />;
};

const AdminPark = () => {
  const { token } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('sections');
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Sections state
  const [sections, setSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({ name: {}, description: {}, image: '', icon: 'Square', isActive: true });
  const [uploadingSectionImage, setUploadingSectionImage] = useState(false);
  
  // Obstacles state
  const [obstacles, setObstacles] = useState([]);
  const [editingObstacle, setEditingObstacle] = useState(null);
  const [obstacleForm, setObstacleForm] = useState({ name: {}, description: {}, image: '', sectionId: '', isActive: true });
  const [obstacleFilterSection, setObstacleFilterSection] = useState('all');
  const [uploadingObstacleImage, setUploadingObstacleImage] = useState(false);
  
  // Amenities state
  const [amenities, setAmenities] = useState([]);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [amenityForm, setAmenityForm] = useState({ name: {}, description: {}, icon: 'Star', isActive: true });
  
  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Existing uploads (for image picker)
  const [existingUploads, setExistingUploads] = useState([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null); // 'section' or 'gallery'

  // Pre-compute section options for dropdowns (fixes Firefox/Safari rendering issue)
  const sectionOptions = useMemo(() => {
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
      return [];
    }
    return sections.map((s, idx) => ({
      id: String(s._id || s.id || `section-${idx}`),
      name: s.name?.pl || s.name?.en || `Sekcja ${idx + 1}`
    }));
  }, [sections]);

  useEffect(() => {
    fetchData();
    fetchExistingUploads();
  }, []);

  const fetchExistingUploads = async () => {
    try {
      // Fetch all uploads from different sources
      const [parkGallery, classesGallery, heroRes, sliderRes] = await Promise.all([
        axios.get(`${API}/api/park/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/classes/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/hero`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/slider`).catch(() => ({ data: [] }))
      ]);
      
      const allImages = [
        ...parkGallery.data.map(i => i.url),
        ...classesGallery.data.map(i => i.url),
        ...heroRes.data.filter(h => h.mediaType === 'image').map(h => h.mediaUrl),
        ...sliderRes.data.filter(s => s.image).map(s => s.image)
      ].filter(Boolean);
      
      // Remove duplicates
      const uniqueImages = [...new Set(allImages)];
      setExistingUploads(uniqueImages);
    } catch (error) {
      console.error('Error fetching existing uploads:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [langRes, sectionsRes, obstaclesRes, amenitiesRes, galleryRes] = await Promise.all([
        // Use admin endpoint to get ALL languages including inactive
        axios.get(`${API}/api/admin/languages`, { headers: { Authorization: `Bearer ${token}` } })
          .catch(() => axios.get(`${API}/api/languages`)),
        axios.get(`${API}/api/park/admin/sections`, { headers: { Authorization: `Bearer ${token}` } })
          .catch((err) => {
            console.warn('Admin sections failed, trying public endpoint:', err.message);
            return axios.get(`${API}/api/park/sections`);
          }),
        axios.get(`${API}/api/park/admin/obstacles`, { headers: { Authorization: `Bearer ${token}` } })
          .catch((err) => {
            console.warn('Admin obstacles failed, trying public endpoint:', err.message);
            return axios.get(`${API}/api/park/obstacles`);
          }),
        axios.get(`${API}/api/park/admin/amenities`, { headers: { Authorization: `Bearer ${token}` } })
          .catch((err) => {
            console.warn('Admin amenities failed, trying public endpoint:', err.message);
            return axios.get(`${API}/api/park/amenities`);
          }),
        axios.get(`${API}/api/park/admin/gallery`, { headers: { Authorization: `Bearer ${token}` } })
          .catch((err) => {
            console.warn('Admin gallery failed, trying public endpoint:', err.message);
            return axios.get(`${API}/api/park/gallery`);
          })
      ]);
      
      // Include ALL languages (active and inactive) for admin editing
      setLanguages(langRes.data.map(l => ({ ...l, isActive: l.isActive !== false })));
      
      // Log sections for debugging
      console.log('[AdminPark] Sections loaded:', sectionsRes.data?.length || 0);
      setSections(sectionsRes.data || []);
      setObstacles(obstaclesRes.data || []);
      setAmenities(amenitiesRes.data || []);
      setGallery(galleryRes.data || []);
    } catch (error) {
      console.error('Error fetching park data:', error);
      toast.error('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  // ============ SECTIONS ============
  
  const handleSectionImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingSectionImage(true);
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API}/api/park/admin/gallery/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSectionForm(prev => ({ ...prev, image: response.data.url }));
      toast.success('Zdjęcie przesłane');
      fetchExistingUploads();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Błąd podczas przesyłania zdjęcia');
    } finally {
      setUploadingSectionImage(false);
      e.target.value = '';
    }
  };

  const handleObstacleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingObstacleImage(true);
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API}/api/park/admin/gallery/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setObstacleForm(prev => ({ ...prev, image: response.data.url }));
      toast.success('Zdjęcie przesłane');
      fetchExistingUploads();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Błąd podczas przesyłania zdjęcia');
    } finally {
      setUploadingObstacleImage(false);
      e.target.value = '';
    }
  };

  const handleSelectExistingImage = (url) => {
    if (imagePickerTarget === 'section') {
      setSectionForm(prev => ({ ...prev, image: url }));
    } else if (imagePickerTarget === 'obstacle') {
      setObstacleForm(prev => ({ ...prev, image: url }));
    }
    setShowImagePicker(false);
    setImagePickerTarget(null);
  };
  
  const handleSaveSection = async () => {
    try {
      setSaving(true);
      if (editingSection) {
        await axios.put(`${API}/api/park/admin/sections/${editingSection}`, sectionForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sekcja zaktualizowana');
      } else {
        await axios.post(`${API}/api/park/admin/sections`, sectionForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sekcja dodana');
      }
      setEditingSection(null);
      setSectionForm({ name: {}, description: {}, image: '', icon: 'Square', isActive: true });
      fetchData();
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę sekcję?')) return;
    
    try {
      await axios.delete(`${API}/api/park/admin/sections/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sekcja usunięta');
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
    }
  };

  const handleEditSection = (section) => {
    setEditingSection(section._id);
    setSectionForm({
      name: section.name || {},
      description: section.description || {},
      image: section.image || '',
      icon: section.icon || 'Square',
      isActive: section.isActive !== false
    });
  };

  // ============ OBSTACLES ============
  
  const handleSaveObstacle = async () => {
    try {
      setSaving(true);
      if (editingObstacle) {
        await axios.put(`${API}/api/park/admin/obstacles/${editingObstacle}`, obstacleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Przeszkoda zaktualizowana');
      } else {
        await axios.post(`${API}/api/park/admin/obstacles`, obstacleForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Przeszkoda dodana');
      }
      setEditingObstacle(null);
      setObstacleForm({ name: {}, description: {}, image: '', sectionId: '', isActive: true });
      fetchData();
    } catch (error) {
      console.error('Error saving obstacle:', error);
      toast.error('Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteObstacle = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę przeszkodę?')) return;
    
    try {
      await axios.delete(`${API}/api/park/admin/obstacles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Przeszkoda usunięta');
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
    }
  };

  const handleEditObstacle = (obstacle) => {
    setEditingObstacle(obstacle._id);
    setObstacleForm({
      name: obstacle.name || {},
      description: obstacle.description || {},
      image: obstacle.image || '',
      sectionId: obstacle.sectionId || '',
      isActive: obstacle.isActive !== false
    });
  };

  const filteredObstacles = obstacleFilterSection === 'all' 
    ? obstacles 
    : obstacles.filter(o => o.sectionId === obstacleFilterSection || (!o.sectionId && obstacleFilterSection === 'none'));

  // ============ AMENITIES ============
  
  const handleSaveAmenity = async () => {
    try {
      setSaving(true);
      if (editingAmenity) {
        await axios.put(`${API}/api/park/admin/amenities/${editingAmenity}`, amenityForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Udogodnienie zaktualizowane');
      } else {
        await axios.post(`${API}/api/park/admin/amenities`, amenityForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Udogodnienie dodane');
      }
      setEditingAmenity(null);
      setAmenityForm({ name: {}, description: {}, icon: 'Star', isActive: true });
      fetchData();
    } catch (error) {
      console.error('Error saving amenity:', error);
      toast.error('Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAmenity = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to udogodnienie?')) return;
    
    try {
      await axios.delete(`${API}/api/park/admin/amenities/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Udogodnienie usunięte');
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
    }
  };

  const handleEditAmenity = (amenity) => {
    setEditingAmenity(amenity._id);
    setAmenityForm({
      name: amenity.name || {},
      description: amenity.description || {},
      icon: amenity.icon || 'Star',
      isActive: amenity.isActive !== false
    });
  };

  // ============ GALLERY ============
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API}/api/park/admin/gallery/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Zdjęcie dodane do galerii');
      fetchData();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Błąd podczas przesyłania zdjęcia');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zdjęcie?')) return;
    
    try {
      await axios.delete(`${API}/api/park/admin/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zdjęcie usunięte');
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
    }
  };

  const toggleGalleryImageActive = async (image) => {
    try {
      await axios.put(`${API}/api/park/admin/gallery/${image._id}`, {
        isActive: !image.isActive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas aktualizacji');
    }
  };

  // ============ REORDER HANDLERS ============
  
  const handleReorderSections = async (newOrder) => {
    setSections(newOrder);
    try {
      await axios.put(`${API}/api/park/admin/sections/reorder`, {
        orderedIds: newOrder.map(s => s._id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kolejność sekcji zapisana');
    } catch (error) {
      toast.error('Błąd podczas zmiany kolejności');
      fetchData();
    }
  };

  const handleReorderObstacles = async (newOrder) => {
    setObstacles(newOrder);
    try {
      await axios.put(`${API}/api/park/admin/obstacles/reorder`, {
        orderedIds: newOrder.map(o => o._id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kolejność przeszkód zapisana');
    } catch (error) {
      toast.error('Błąd podczas zmiany kolejności');
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Park</h1>
          <p className="text-gray-400">Zarządzaj sekcjami, udogodnieniami i galerią parku</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5">
          <TabsTrigger value="sections" className="data-[state=active]:bg-primary">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Sekcje ({sections.length})
          </TabsTrigger>
          <TabsTrigger value="obstacles" className="data-[state=active]:bg-primary">
            <Mountain className="w-4 h-4 mr-2" />
            Przeszkody ({obstacles.length})
          </TabsTrigger>
          <TabsTrigger value="amenities" className="data-[state=active]:bg-primary">
            <Shield className="w-4 h-4 mr-2" />
            Udogodnienia ({amenities.length})
          </TabsTrigger>
          <TabsTrigger value="gallery" className="data-[state=active]:bg-primary">
            <ImageIcon className="w-4 h-4 mr-2" />
            Galeria ({gallery.length})
          </TabsTrigger>
        </TabsList>

        {/* SECTIONS TAB */}
        <TabsContent value="sections" className="space-y-4">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {editingSection ? 'Edytuj sekcję' : 'Dodaj nową sekcję'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Language tabs for name */}
              <div>
                <Label>Nazwa sekcji</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Input
                        value={sectionForm.name[lang.code] || ''}
                        onChange={(e) => setSectionForm(prev => ({
                          ...prev,
                          name: { ...prev.name, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Nazwa (${lang.code})`}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Description */}
              <div>
                <Label>Opis sekcji</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Textarea
                        value={sectionForm.description[lang.code] || ''}
                        onChange={(e) => setSectionForm(prev => ({
                          ...prev,
                          description: { ...prev.description, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Opis (${lang.code})`}
                        className="bg-white/5 border-white/10"
                        rows={3}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Image - Upload or Select */}
              <div>
                <Label>Zdjęcie sekcji</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSectionImageUpload}
                    disabled={uploadingSectionImage}
                    className="hidden"
                    id="section-image-upload"
                  />
                  <label htmlFor="section-image-upload">
                    <Button asChild disabled={uploadingSectionImage} variant="outline">
                      <span>
                        {uploadingSectionImage ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Prześlij z komputera
                      </span>
                    </Button>
                  </label>
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowImagePicker(true); setImagePickerTarget('section'); }}
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Wybierz z istniejących ({existingUploads.length})
                  </Button>
                </div>
                <Input
                  value={sectionForm.image}
                  onChange={(e) => setSectionForm(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="lub wklej URL zdjęcia..."
                  className="bg-white/5 border-white/10 mt-2"
                />
                {sectionForm.image && (
                  <div className="mt-2 relative inline-block">
                    <img 
                      src={sectionForm.image.startsWith('/') ? `${API}${sectionForm.image}` : sectionForm.image} 
                      alt="Preview" 
                      className="h-32 object-cover rounded"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1 right-1 bg-black/50 hover:bg-black/70"
                      onClick={() => setSectionForm(prev => ({ ...prev, image: '' }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Icon selector */}
              <div>
                <Label>Ikona sekcji</Label>
                <div className="flex flex-wrap gap-2 mt-2 p-3 bg-white/5 rounded-lg max-h-40 overflow-y-auto">
                  {AVAILABLE_ICONS.map(iconName => (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setSectionForm(prev => ({ ...prev, icon: iconName }))}
                      className={`p-2 rounded transition-colors ${
                        sectionForm.icon === iconName 
                          ? 'bg-primary text-white' 
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      title={iconName}
                    >
                      <IconComponent name={iconName} className="w-5 h-5" />
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">Wybrana ikona: {sectionForm.icon || 'Square'}</p>
              </div>
              
              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={sectionForm.isActive}
                  onCheckedChange={(checked) => setSectionForm(prev => ({ ...prev, isActive: checked }))}
                />
                <Label>Aktywna</Label>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleSaveSection} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingSection ? 'Zapisz zmiany' : 'Dodaj sekcję'}
                </Button>
                {editingSection && (
                  <Button variant="outline" onClick={() => {
                    setEditingSection(null);
                    setSectionForm({ name: {}, description: {}, image: '', icon: 'Square', isActive: true });
                  }}>
                    <X className="w-4 h-4 mr-2" />
                    Anuluj
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sections list - sortable */}
          <SortableList
            items={sections}
            onReorder={handleReorderSections}
            idPrefix="section"
            renderItem={(section) => (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <IconComponent name={section.icon || 'Square'} className="w-5 h-5 text-primary" />
                    </div>
                    
                    {section.image && (
                      <img 
                        src={section.image.startsWith('/') ? `${API}${section.image}` : section.image}
                        alt={section.name?.pl || 'Section'}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    
                    <div className="flex-1">
                      <h3 className="font-medium">{section.name?.pl || 'Bez nazwy'}</h3>
                      <p className="text-sm text-gray-400">{section.description?.pl?.slice(0, 100)}...</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {section.isActive ? (
                        <Eye className="w-4 h-4 text-green-500" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      )}
                      
                      <Button variant="ghost" size="sm" onClick={() => handleEditSection(section)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteSection(section._id)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          />
          {sections.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-8 text-center text-gray-400">
                Brak sekcji. Dodaj pierwszą sekcję powyżej.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* OBSTACLES TAB */}
        <TabsContent value="obstacles" className="space-y-4">
          {/* Filter by section */}
          <div className="flex items-center gap-4">
            <Label>Filtruj po sekcji:</Label>
            <Select value={obstacleFilterSection} onValueChange={setObstacleFilterSection}>
              <SelectTrigger className="w-[250px] bg-white/5 border-white/10">
                <SelectValue placeholder="Wybierz sekcję" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/10">
                <SelectItem value="all">Wszystkie sekcje</SelectItem>
                <SelectItem value="none">Bez przypisanej sekcji</SelectItem>
                {sectionOptions.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sections.length === 0 && (
              <span className="text-xs text-gray-400">(brak sekcji do filtrowania)</span>
            )}
          </div>

          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {editingObstacle ? 'Edytuj przeszkodę' : 'Dodaj nową przeszkodę'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Section selector */}
              <div>
                <Label>Sekcja {sectionOptions.length > 0 ? `(${sectionOptions.length} dostępnych)` : '(ładowanie...)'}</Label>
                <Select 
                  value={obstacleForm.sectionId || 'none'} 
                  onValueChange={(val) => setObstacleForm(prev => ({ ...prev, sectionId: val === 'none' ? null : val }))}
                >
                  <SelectTrigger className="w-full mt-2 bg-white/5 border-white/10">
                    <SelectValue placeholder="Wybierz sekcję" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/10">
                    <SelectItem value="none">-- Bez przypisanej sekcji --</SelectItem>
                    {sectionOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {sectionOptions.length === 0 && !loading && (
                  <p className="text-xs text-yellow-500 mt-1">⚠️ Brak sekcji - najpierw dodaj sekcje w zakładce "Sekcje"</p>
                )}
              </div>

              {/* Name */}
              <div>
                <Label>Nazwa przeszkody</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Input
                        value={obstacleForm.name[lang.code] || ''}
                        onChange={(e) => setObstacleForm(prev => ({
                          ...prev,
                          name: { ...prev.name, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Nazwa (${lang.code})`}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Description */}
              <div>
                <Label>Opis przeszkody</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Textarea
                        value={obstacleForm.description[lang.code] || ''}
                        onChange={(e) => setObstacleForm(prev => ({
                          ...prev,
                          description: { ...prev.description, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Opis (${lang.code})`}
                        className="bg-white/5 border-white/10"
                        rows={3}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Image */}
              <div>
                <Label>Zdjęcie przeszkody</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => document.getElementById('obstacleImageUpload').click()}
                    disabled={uploadingObstacleImage}
                  >
                    {uploadingObstacleImage ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Wgraj zdjęcie
                  </Button>
                  <input
                    type="file"
                    id="obstacleImageUpload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleObstacleImageUpload}
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowImagePicker(true); setImagePickerTarget('obstacle'); }}
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Wybierz z istniejących ({existingUploads.length})
                  </Button>
                </div>
                <Input
                  value={obstacleForm.image}
                  onChange={(e) => setObstacleForm(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="lub wklej URL zdjęcia..."
                  className="bg-white/5 border-white/10 mt-2"
                />
                {obstacleForm.image && (
                  <div className="mt-2 relative inline-block">
                    <img 
                      src={obstacleForm.image.startsWith('/') ? `${API}${obstacleForm.image}` : obstacleForm.image} 
                      alt="Preview" 
                      className="h-32 object-cover rounded"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1 right-1 bg-black/50 hover:bg-black/70"
                      onClick={() => setObstacleForm(prev => ({ ...prev, image: '' }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={obstacleForm.isActive}
                  onCheckedChange={(checked) => setObstacleForm(prev => ({ ...prev, isActive: checked }))}
                />
                <Label>Aktywna</Label>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleSaveObstacle} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingObstacle ? 'Zapisz zmiany' : 'Dodaj przeszkodę'}
                </Button>
                {editingObstacle && (
                  <Button variant="outline" onClick={() => {
                    setEditingObstacle(null);
                    setObstacleForm({ name: {}, description: {}, image: '', sectionId: '', isActive: true });
                  }}>
                    <X className="w-4 h-4 mr-2" />
                    Anuluj
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Obstacles list - sortable */}
          {filteredObstacles.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-8 text-center text-gray-400">
                Brak przeszkód {obstacleFilterSection !== 'all' && 'w wybranej sekcji'}
              </CardContent>
            </Card>
          ) : (
            <SortableList
              items={filteredObstacles}
              onReorder={(newOrder) => {
                // Update full obstacles array maintaining unfiltered items
                if (obstacleFilterSection === 'all') {
                  handleReorderObstacles(newOrder);
                } else {
                  const otherObstacles = obstacles.filter(o => !filteredObstacles.some(f => f._id === o._id));
                  handleReorderObstacles([...newOrder, ...otherObstacles]);
                }
              }}
              idPrefix="obstacle"
              renderItem={(obstacle) => {
                const section = sections.find(s => s._id === obstacle.sectionId);
                return (
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        {obstacle.image ? (
                          <img 
                            src={obstacle.image.startsWith('/') ? `${API}${obstacle.image}` : obstacle.image}
                            alt={obstacle.name?.pl || 'Obstacle'}
                            className="w-20 h-20 object-cover rounded"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-white/10 rounded flex items-center justify-center">
                            <Mountain className="w-8 h-8 text-gray-500" />
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <h3 className="font-medium">{obstacle.name?.pl || 'Bez nazwy'}</h3>
                          <p className="text-sm text-gray-400">{obstacle.description?.pl?.slice(0, 80)}...</p>
                          {section && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">
                              <IconComponent name={section.icon || 'Square'} className="w-3 h-3" />
                              {section.name?.pl}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {obstacle.isActive ? (
                            <Eye className="w-4 h-4 text-green-500" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-gray-500" />
                          )}
                          
                          <Button variant="ghost" size="sm" onClick={() => handleEditObstacle(obstacle)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteObstacle(obstacle._id)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }}
            />
          )}
        </TabsContent>

        {/* AMENITIES TAB */}
        <TabsContent value="amenities" className="space-y-4">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {editingAmenity ? 'Edytuj udogodnienie' : 'Dodaj nowe udogodnienie'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Icon selector */}
              <div>
                <Label>Ikona</Label>
                <div className="flex flex-wrap gap-1 mt-2 max-h-40 overflow-y-auto p-2 bg-white/5 rounded-lg">
                  {AVAILABLE_ICONS.map(iconName => (
                    <Button
                      key={iconName}
                      type="button"
                      variant={amenityForm.icon === iconName ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setAmenityForm(prev => ({ ...prev, icon: iconName }))}
                      className={`w-10 h-10 p-0 ${amenityForm.icon === iconName ? 'ring-2 ring-primary' : ''}`}
                      title={iconName}
                    >
                      <IconComponent name={iconName} className="w-5 h-5" />
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                  Wybrana ikona: <IconComponent name={amenityForm.icon} className="w-4 h-4" /> {amenityForm.icon}
                </p>
              </div>
              
              {/* Name */}
              <div>
                <Label>Nazwa</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Input
                        value={amenityForm.name[lang.code] || ''}
                        onChange={(e) => setAmenityForm(prev => ({
                          ...prev,
                          name: { ...prev.name, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Nazwa (${lang.code})`}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Description */}
              <div>
                <Label>Opis (opcjonalnie)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Input
                        value={amenityForm.description[lang.code] || ''}
                        onChange={(e) => setAmenityForm(prev => ({
                          ...prev,
                          description: { ...prev.description, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Opis (${lang.code})`}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={amenityForm.isActive}
                  onCheckedChange={(checked) => setAmenityForm(prev => ({ ...prev, isActive: checked }))}
                />
                <Label>Aktywne</Label>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleSaveAmenity} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingAmenity ? 'Zapisz zmiany' : 'Dodaj udogodnienie'}
                </Button>
                {editingAmenity && (
                  <Button variant="outline" onClick={() => {
                    setEditingAmenity(null);
                    setAmenityForm({ name: {}, description: {}, icon: 'Star', isActive: true });
                  }}>
                    <X className="w-4 h-4 mr-2" />
                    Anuluj
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Amenities list */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {amenities.map(amenity => (
              <Card key={amenity._id} className={`bg-white/5 border-white/10 ${!amenity.isActive ? 'opacity-50' : ''}`}>
                <CardContent className="py-4 text-center">
                  <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-primary/20 flex items-center justify-center">
                    <IconComponent name={amenity.icon} className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-medium">{amenity.name?.pl || 'Bez nazwy'}</h3>
                  {amenity.description?.pl && (
                    <p className="text-xs text-gray-400 mt-1">{amenity.description.pl}</p>
                  )}
                  <div className="flex justify-center gap-2 mt-3">
                    <Button variant="ghost" size="sm" onClick={() => handleEditAmenity(amenity)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteAmenity(amenity._id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {amenities.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-8 text-center text-gray-400">
                Brak udogodnień. Dodaj pierwsze udogodnienie powyżej.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* GALLERY TAB */}
        <TabsContent value="gallery" className="space-y-4">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="hidden"
                  id="gallery-upload"
                />
                <label htmlFor="gallery-upload">
                  <Button asChild disabled={uploadingImage}>
                    <span>
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Dodaj zdjęcie
                    </span>
                  </Button>
                </label>
                <p className="text-sm text-gray-400">
                  Obsługiwane formaty: JPG, PNG, GIF, WebP. Maksymalny rozmiar: 10MB
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Gallery grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {gallery.map(image => (
              <div key={image._id} className="relative group">
                <img
                  src={image.url.startsWith('/') ? `${API}${image.url}` : image.url}
                  alt={image.alt?.pl || 'Gallery image'}
                  className={`w-full h-32 object-cover rounded-lg ${!image.isActive ? 'opacity-50' : ''}`}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleGalleryImageActive(image)}
                    className="text-white hover:bg-white/20"
                  >
                    {image.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteGalleryImage(image._id)}
                    className="text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {gallery.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-8 text-center text-gray-400">
                Galeria jest pusta. Dodaj pierwsze zdjęcie powyżej.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Image Picker Dialog */}
      <Dialog open={showImagePicker} onOpenChange={setShowImagePicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Wybierz z istniejących zdjęć</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
            {existingUploads.map((url, idx) => (
              <button
                key={idx}
                onClick={() => handleSelectExistingImage(url)}
                className="aspect-square overflow-hidden rounded-lg hover:ring-2 ring-primary transition-all"
              >
                <img
                  src={url.startsWith('/') ? `${API}${url}` : url}
                  alt={`Upload ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
            {existingUploads.length === 0 && (
              <p className="col-span-full text-center text-gray-400 py-8">
                Brak wcześniej przesłanych zdjęć
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPark;
